import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { OutletStatusSchema } from './restaurants';

/* ------------------------------------------------------------------ */
/* Partner subscriptions (W6)                                          */
/*                                                                     */
/* Every outlet starts in a free launch phase: no plan fee and no      */
/* commission until it has had N orders or N days (settings), which-   */
/* ever comes first. Then it pays a monthly plan (Starter, Growth,     */
/* Town Hero, Legend). Each plan includes an order allowance; orders   */
/* beyond it cost a small fee each. The outlet is never switched off   */
/* for going over; it is nudged to upgrade instead.                    */
/*                                                                     */
/* Owners may also choose a voluntary commission % on their orders.    */
/* Bills are made at month end (in arrears) with GST 18% (India) or    */
/* VAT 13% (Nepal) on Capsico's fees. Money is always in minor units.  */
/*                                                                     */
/* Plans are versioned: a published version never changes. Editing a   */
/* plan makes a draft; publishing it replaces the old version for new  */
/* choices. Outlets already on a version keep it until their plan      */
/* changes.                                                            */
/* ------------------------------------------------------------------ */

export const PLAN_KEYS = ['starter', 'growth', 'town_hero', 'legend'] as const;
export const PlanKeySchema = z.enum(PLAN_KEYS);
export type PlanKey = z.infer<typeof PlanKeySchema>;
export const PLAN_LABELS: Record<PlanKey, string> = {
  starter: 'Starter',
  growth: 'Growth',
  town_hero: 'Town Hero',
  legend: 'Legend',
};

export const PLAN_VERSION_STATUSES = ['draft', 'published', 'retired'] as const;
export const PlanVersionStatusSchema = z.enum(PLAN_VERSION_STATUSES);
export type PlanVersionStatus = z.infer<typeof PlanVersionStatusSchema>;
export const PLAN_VERSION_STATUS_LABELS: Record<PlanVersionStatus, string> = {
  draft: 'Draft',
  published: 'Live',
  retired: 'Old version',
};

/** Largest plan fee or per-order fee: ₹ / Rs 1,00,000. */
export const MAX_PLAN_MONEY_MINOR = 100_000_00;
const PlanMoneySchema = z
  .number()
  .int('Use whole paise / paisa')
  .min(0)
  .max(MAX_PLAN_MONEY_MINOR, 'That amount is too high');

export const PlanVersionSchema = z.object({
  id: z.uuid(),
  planId: z.uuid(),
  versionNo: z.number().int().min(1),
  status: PlanVersionStatusSchema,
  monthlyFeeMinor: z.number().int(),
  orderAllowance: z.number().int(),
  overageFeeMinor: z.number().int(),
  features: z.array(z.string()),
  note: z.string().nullable(),
  /** Outlets on this version right now. */
  subscribers: z.number().int(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  publishedAt: z.string().nullable(),
  publishedByName: z.string().nullable(),
  version: z.number().int(),
});
export type PlanVersion = z.infer<typeof PlanVersionSchema>;

export const PlanSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema,
  key: PlanKeySchema,
  name: z.string(),
  sortOrder: z.number().int(),
  /** The version outlets choose today; null only before the first publish. */
  live: PlanVersionSchema.nullable(),
  draft: PlanVersionSchema.nullable(),
  history: z.array(PlanVersionSchema),
});
export type Plan = z.infer<typeof PlanSchema>;

export const PlanListQuerySchema = z.object({ country: CountryCodeSchema });
export type PlanListQuery = z.infer<typeof PlanListQuerySchema>;

export const PlanDraftInputSchema = z.object({
  monthlyFeeMinor: PlanMoneySchema.refine((v) => v > 0, 'A plan needs a monthly fee'),
  orderAllowance: z
    .number()
    .int('Use a whole number of orders')
    .min(1, 'Include at least one order')
    .max(100_000, 'That allowance is too high'),
  overageFeeMinor: PlanMoneySchema,
  features: z
    .array(z.string().trim().min(2, 'Too short').max(80, 'Keep each line under 80 characters'))
    .max(8, 'At most 8 lines'),
  note: z.string().trim().max(300).nullable(),
});
export type PlanDraftInput = z.infer<typeof PlanDraftInputSchema>;

export const UpdatePlanDraftInputSchema = PlanDraftInputSchema.extend({
  expectedVersion: z.number().int().min(1),
});
export type UpdatePlanDraftInput = z.infer<typeof UpdatePlanDraftInputSchema>;

export const PublishPlanInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
});
export type PublishPlanInput = z.infer<typeof PublishPlanInputSchema>;

/* ---------------- subscriptions ---------------- */

export const SUBSCRIPTION_STATUSES = ['launch', 'active', 'cancelled'] as const;
export const SubscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  launch: 'Free launch',
  active: 'On a plan',
  cancelled: 'Ended',
};

export const LAUNCH_END_REASONS = ['orders', 'days', 'early'] as const;
export const LaunchEndReasonSchema = z.enum(LAUNCH_END_REASONS);
export type LaunchEndReason = z.infer<typeof LaunchEndReasonSchema>;
export const LAUNCH_END_REASON_LABELS: Record<LaunchEndReason, string> = {
  orders: 'Reached the free order count',
  days: 'Free days ended',
  early: 'Ended early by Ops',
};

export const LaunchProgressSchema = z.object({
  startedAt: z.string(),
  endsAt: z.string(),
  orderLimit: z.number().int(),
  orders: z.number().int(),
  daysLeft: z.number().int(),
  ordersLeft: z.number().int(),
  endedAt: z.string().nullable(),
  endReason: LaunchEndReasonSchema.nullable(),
});
export type LaunchProgress = z.infer<typeof LaunchProgressSchema>;

export const PlanRefSchema = z.object({
  versionId: z.uuid(),
  key: PlanKeySchema,
  name: z.string(),
  monthlyFeeMinor: z.number().int(),
  orderAllowance: z.number().int(),
  overageFeeMinor: z.number().int(),
  /** False when the outlet is on an older version of the plan. */
  isLatest: z.boolean(),
});
export type PlanRef = z.infer<typeof PlanRefSchema>;

/** This month so far, and where it is heading at the current pace. */
export const UsageSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  orders: z.number().int(),
  /** Allowance for this month, adjusted for days on each plan. */
  allowance: z.number().int(),
  overOrders: z.number().int(),
  overageSoFarMinor: z.number().int(),
  projectedOrders: z.number().int(),
  projectedOverageMinor: z.number().int(),
  feeSoFarMinor: z.number().int(),
  commissionSoFarMinor: z.number().int(),
  percentUsed: z.number().int(),
});
export type Usage = z.infer<typeof UsageSchema>;

export const UpgradeNudgeSchema = z.object({
  planKey: PlanKeySchema,
  planName: z.string(),
  /** What the bigger plan would save this month at the current pace (0 when it costs more). */
  savesMinor: z.number().int(),
  message: z.string(),
});
export type UpgradeNudge = z.infer<typeof UpgradeNudgeSchema>;

export const SubscriptionRowSchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  outletName: z.string(),
  outletStatus: OutletStatusSchema,
  restaurantId: z.uuid(),
  restaurantName: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  status: SubscriptionStatusSchema,
  plan: PlanRefSchema.nullable(),
  pendingPlan: PlanRefSchema.nullable(),
  commissionPercent: z.number(),
  launch: LaunchProgressSchema,
  usage: UsageSchema.nullable(),
  owedMinor: z.number().int(),
  owedInvoices: z.number().int(),
  version: z.number().int(),
});
export type SubscriptionRow = z.infer<typeof SubscriptionRowSchema>;

export const SUBSCRIPTION_VIEWS = ['all', 'launch', 'over', 'owed'] as const;
export const SubscriptionViewSchema = z.enum(SUBSCRIPTION_VIEWS);
export type SubscriptionView = z.infer<typeof SubscriptionViewSchema>;
export const SUBSCRIPTION_VIEW_LABELS: Record<SubscriptionView, string> = {
  all: 'All',
  launch: 'Free launch',
  over: 'Over allowance',
  owed: 'Money owed',
};

export const SubscriptionListQuerySchema = z.object({
  view: SubscriptionViewSchema.default('all'),
  townId: z.uuid().optional(),
  country: CountryCodeSchema.optional(),
  search: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type SubscriptionListQuery = z.infer<typeof SubscriptionListQuerySchema>;

export const SubscriptionListSchema = z.object({
  rows: z.array(SubscriptionRowSchema),
  counts: z.record(SubscriptionViewSchema, z.number().int()),
});
export type SubscriptionList = z.infer<typeof SubscriptionListSchema>;

export const SubscriptionEventSchema = z.object({
  id: z.uuid(),
  action: z.string(),
  summary: z.string(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
});
export type SubscriptionEvent = z.infer<typeof SubscriptionEventSchema>;

/* ---------------- invoices ---------------- */

export const INVOICE_STATUSES = ['owed', 'paid', 'deducted', 'void'] as const;
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  owed: 'Owed',
  paid: 'Paid',
  deducted: 'Taken from payout',
  void: 'Cancelled',
};

export const INVOICE_LINE_KINDS = ['plan_fee', 'overage', 'commission'] as const;
export const InvoiceLineKindSchema = z.enum(INVOICE_LINE_KINDS);
export type InvoiceLineKind = z.infer<typeof InvoiceLineKindSchema>;

export const InvoiceLineSchema = z.object({
  kind: InvoiceLineKindSchema,
  label: z.string(),
  detail: z.string().nullable(),
  quantity: z.number(),
  unitMinor: z.number().int(),
  amountMinor: z.number().int(),
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const TaxLineSchema = z.object({
  label: z.string(),
  ratePercent: z.number(),
  amountMinor: z.number().int(),
});
export type TaxLine = z.infer<typeof TaxLineSchema>;

export const InvoicePartySchema = z.object({
  name: z.string(),
  taxId: z.string().nullable(),
  address: z.string().nullable(),
});
export type InvoiceParty = z.infer<typeof InvoicePartySchema>;

export const InvoiceSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  country: CountryCodeSchema,
  subscriptionId: z.uuid(),
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  townName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  status: InvoiceStatusSchema,
  lines: z.array(InvoiceLineSchema),
  subtotalMinor: z.number().int(),
  taxLines: z.array(TaxLineSchema),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  issuer: InvoicePartySchema,
  buyer: InvoicePartySchema,
  issuedAt: z.string(),
  dueAt: z.string(),
  paidAt: z.string().nullable(),
  paidReference: z.string().nullable(),
  paidNote: z.string().nullable(),
  paidByName: z.string().nullable(),
  deductedAt: z.string().nullable(),
  payoutReference: z.string().nullable(),
  version: z.number().int(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const INVOICE_VIEWS = ['owed', 'overdue', 'settled', 'all'] as const;
export const InvoiceViewSchema = z.enum(INVOICE_VIEWS);
export type InvoiceView = z.infer<typeof InvoiceViewSchema>;
export const INVOICE_VIEW_LABELS: Record<InvoiceView, string> = {
  owed: 'Owed',
  overdue: 'Past due',
  settled: 'Settled',
  all: 'All',
};

export const InvoiceListQuerySchema = z.object({
  view: InvoiceViewSchema.default('owed'),
  townId: z.uuid().optional(),
  country: CountryCodeSchema.optional(),
  subscriptionId: z.uuid().optional(),
  search: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;

export const InvoiceListSchema = z.object({
  rows: z.array(InvoiceSchema),
  counts: z.record(InvoiceViewSchema, z.number().int()),
  owedMinorByCountry: z.record(CountryCodeSchema, z.number().int()),
});
export type InvoiceList = z.infer<typeof InvoiceListSchema>;

export const MarkInvoicePaidInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reference: z.string().trim().min(3, 'Enter the payment reference (UPI / bank / receipt)').max(80),
  paidOn: z.iso.date('Pick the date the money arrived'),
  note: z.string().trim().max(300).nullable(),
});
export type MarkInvoicePaidInput = z.infer<typeof MarkInvoicePaidInputSchema>;

/* ---------------- detail + actions ---------------- */

export const SubscriptionDetailSchema = SubscriptionRowSchema.extend({
  commissionMaxPercent: z.number(),
  upgradeNudge: UpgradeNudgeSchema.nullable(),
  /** Live plans the outlet can move to (its country). */
  plans: z.array(PlanRefSchema),
  invoices: z.array(InvoiceSchema),
  events: z.array(SubscriptionEventSchema),
  canManage: z.boolean(),
});
export type SubscriptionDetail = z.infer<typeof SubscriptionDetailSchema>;

export const PLAN_CHANGE_TIMINGS = ['now', 'next_month'] as const;
export const PlanChangeTimingSchema = z.enum(PLAN_CHANGE_TIMINGS);
export type PlanChangeTiming = z.infer<typeof PlanChangeTimingSchema>;

export const ChangePlanInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  planKey: PlanKeySchema,
  /** Defaults: bigger plans start now, smaller plans next month. Only Ops may override. */
  timing: PlanChangeTimingSchema.optional(),
  reason: z.string().trim().max(200).optional(),
});
export type ChangePlanInput = z.infer<typeof ChangePlanInputSchema>;

export const SetCommissionInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  percent: z
    .number()
    .min(0, 'Use 0 for no commission')
    .max(100)
    .refine((v) => Math.round(v * 100) === v * 100, 'At most two decimals'),
  reason: z.string().trim().max(200).optional(),
});
export type SetCommissionInput = z.infer<typeof SetCommissionInputSchema>;

export const EndLaunchInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
});
export type EndLaunchInput = z.infer<typeof EndLaunchInputSchema>;

/** Development only: pretend orders happened, to try billing before W8 orders exist. */
export const TestOrdersInputSchema = z.object({
  count: z.number().int().min(1).max(500),
  orderValueMinor: z.number().int().min(0).max(100_000_00),
});
export type TestOrdersInput = z.infer<typeof TestOrdersInputSchema>;

export const SubscriptionSweepResultSchema = z.object({
  created: z.number().int(),
  launchesEnded: z.number().int(),
  invoicesIssued: z.number().int(),
  monthsClosed: z.number().int(),
  plansSwitched: z.number().int(),
  overdueInvoices: z.number().int(),
});
export type SubscriptionSweepResult = z.infer<typeof SubscriptionSweepResultSchema>;

/** Commission shown with up to two decimals: "2.5%". */
export function formatPercent(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace(/0$/, '')}%`;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * "Oct 2026" for a billing month. Months start at local midnight in India / Nepal (18:30 or
 * 18:15 UTC the day before), so half a day later is always inside the month in UTC.
 */
export function formatBillingMonth(periodStartIso: string): string {
  const date = new Date(new Date(periodStartIso).getTime() + 12 * 3_600_000);
  if (Number.isNaN(date.getTime())) return '—';
  return `${MONTH_NAMES[date.getUTCMonth()] ?? ''} ${date.getUTCFullYear()}`;
}
